import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, AlertTriangle } from "lucide-react";
import { Card, PageHeader, Btn, Field, inputCls, CheckboxGroup } from "../../components/dash/Kit";
import { api, errMessage } from "../../lib/api";

const TIMES = ["08:00", "10:00", "12:00", "14:00", "16:00", "18:00", "20:00"];

const TOGGLES = [
  ["approval_required", "Ask me to approve every post"],
  ["allow_promotions", "May write about promotions"],
  ["allow_offers", "May write about offers"],
  ["allow_prices", "May mention prices I've confirmed"],
  ["allow_emojis", "May use emoji"],
  ["allow_hashtags", "May use hashtags"],
];

export default function AutoPilot() {
  const [state, setState] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const load = () =>
    api
      .get("/social/autopilot")
      .then((r) => {
        setState(r.data);
        setCfg(r.data.settings);
      })
      .catch(() => setState(false));

  useEffect(() => {
    load();
  }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async (patch = {}) => {
    setSaving(true);
    try {
      const { data } = await api.put("/social/autopilot", { ...cfg, ...patch });
      setCfg(data);
      toast.success("Your AutoPilot settings are saved.");
    } catch (e) {
      toast.error(errMessage(e));
      load();
    } finally {
      setSaving(false);
    }
  };

  if (state === null || !cfg) return <Card testId="autopilot-loading">Loading AutoPilot…</Card>;
  if (state === false) return <Card testId="autopilot-error">We couldn't load your AutoPilot settings.</Card>;

  return (
    <div className="space-y-6" data-testid="autopilot-page">
      <PageHeader
        title="AutoPilot"
        body="Decide how much PostMint may do on its own. By default you approve everything before it goes out."
      />

      <Card testId="autopilot-toggle-card" className={cfg.enabled ? "border-[#F95738]" : ""}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-xl">
            <p className="font-display text-lg font-semibold text-slate-900">Trusted AutoPilot</p>
            <p className="mt-2 text-sm text-slate-600">
              When this is on, PostMint may approve and schedule content that fits the rules below — without asking you
              each time.
            </p>
          </div>
          <span
            className={`rounded-full px-3 py-1.5 text-xs font-bold ${
              cfg.enabled ? "bg-[#F95738] text-white" : "bg-slate-100 text-slate-600"
            }`}
            data-testid="autopilot-state-badge"
          >
            {cfg.enabled ? "ON" : "OFF"}
          </span>
        </div>

        {!state.can_autopilot && (
          <p className="mt-4 rounded-xl bg-slate-50 p-3 text-sm text-slate-600" data-testid="autopilot-upgrade-notice">
            Trusted AutoPilot is available on Grow and Pro.
          </p>
        )}

        {!cfg.enabled && confirming && (
          <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="autopilot-warning">
            <p className="flex items-center gap-2 text-sm font-semibold text-amber-900">
              <AlertTriangle className="h-4 w-4" aria-hidden="true" />
              Before you switch this on
            </p>
            <p className="mt-2 text-sm text-amber-900/85">
              With AutoPilot on, content may be published to your connected accounts without you approving each post
              individually. You remain responsible for what appears on your channels. You can switch it off at any time.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Btn
                disabled={saving}
                onClick={async () => {
                  await save({ enabled: true });
                  setConfirming(false);
                }}
                testId="autopilot-confirm-enable"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                I understand — turn AutoPilot on
              </Btn>
              <Btn variant="outline" onClick={() => setConfirming(false)} testId="autopilot-cancel-enable">
                Keep approving manually
              </Btn>
            </div>
          </div>
        )}

        <div className="mt-5">
          {cfg.enabled ? (
            <Btn
              variant="danger"
              disabled={saving}
              onClick={() => save({ enabled: false })}
              testId="autopilot-disable-button"
            >
              Turn AutoPilot off
            </Btn>
          ) : (
            <Btn
              disabled={saving || !state.can_autopilot || confirming}
              onClick={() => setConfirming(true)}
              testId="autopilot-enable-button"
            >
              Turn AutoPilot on
            </Btn>
          )}
        </div>
      </Card>

      <Card testId="autopilot-rules-card">
        <p className="font-display text-lg font-semibold text-slate-900">When should we post?</p>

        <div className="mt-5 grid gap-5 lg:grid-cols-2">
          <Field label="Days we may post">
            <CheckboxGroup
              name="autopilot-days"
              options={state.days}
              value={cfg.preferred_days || []}
              onChange={(v) => set("preferred_days", v)}
              columns={2}
            />
          </Field>
          <Field label="Days we must never post">
            <CheckboxGroup
              name="autopilot-blocked-days"
              options={state.days}
              value={cfg.blocked_days || []}
              onChange={(v) => set("blocked_days", v)}
              columns={2}
            />
          </Field>
          <Field label="Times we may post">
            <CheckboxGroup
              name="autopilot-times"
              options={TIMES}
              value={cfg.preferred_times || []}
              onChange={(v) => set("preferred_times", v)}
              columns={3}
            />
          </Field>
          <div className="space-y-4">
            <Field label="Maximum posts per week">
              <input
                type="number"
                min="1"
                max="21"
                value={cfg.posts_per_week}
                onChange={(e) => set("posts_per_week", Number(e.target.value))}
                data-testid="autopilot-posts-per-week"
                className={inputCls}
              />
            </Field>
            <Field label="Minimum hours between posts">
              <input
                type="number"
                min="1"
                max="168"
                value={cfg.min_hours_between_posts}
                onChange={(e) => set("min_hours_between_posts", Number(e.target.value))}
                data-testid="autopilot-min-hours"
                className={inputCls}
              />
            </Field>
          </div>
        </div>

        <p className="mt-6 text-xs text-slate-500">
          We call these recommended publishing times. Once you have enough real performance data, PostMint will refine
          them instead of guessing.
        </p>
      </Card>

      <Card testId="autopilot-content-rules-card">
        <p className="font-display text-lg font-semibold text-slate-900">What may we write?</p>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          {TOGGLES.map(([key, label]) => (
            <label key={key} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 text-sm text-slate-800">
              <input
                type="checkbox"
                checked={!!cfg[key]}
                onChange={(e) => set(key, e.target.checked)}
                data-testid={`autopilot-${key.replace(/_/g, "-")}`}
                className="h-4 w-4 accent-[#F95738]"
              />
              {label}
            </label>
          ))}
        </div>

        <div className="mt-5 grid gap-5 lg:grid-cols-2">
          <Field label="Topics to stay away from">
            <textarea
              rows={3}
              value={cfg.sensitive_topics || ""}
              onChange={(e) => set("sensitive_topics", e.target.value)}
              data-testid="autopilot-sensitive-topics"
              className={inputCls}
            />
          </Field>
          <Field label="Words we should never use" hint="Comma separated.">
            <textarea
              rows={3}
              value={cfg.blocked_words || ""}
              onChange={(e) => set("blocked_words", e.target.value)}
              data-testid="autopilot-blocked-words"
              className={inputCls}
            />
          </Field>
        </div>

        <Btn className="mt-6" disabled={saving} onClick={() => save()} testId="autopilot-save-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Save my rules
        </Btn>
      </Card>
    </div>
  );
}
